import { lex } from "./lexer.js";
export class ParseError extends Error {
    pos;
    constructor(message, pos) {
        super(message);
        this.pos = pos;
        this.name = "ParseError";
    }
}
/**
 * Grammar (whitespace-separated, case-insensitive keywords):
 *
 *   query    := orExpr? sort?
 *   orExpr   := andExpr ("or" andExpr)*
 *   andExpr  := unit+
 *   unit     := "(" orExpr ")" | attr | shortcut | text
 *   attr     := name ":" value ("," value)*
 *   value    := "-"? atom (".." atom)? | ".." atom
 *   shortcut := "#" atom
 *   text     := word
 *   sort     := ("sort" | "order") "by" ":" sortKey ("," sortKey)*
 *   sortKey  := name ("asc" | "desc")?
 *   name     := word | quoted | braced
 *   atom     := word | quoted | braced
 *
 * Several values on one attribute: positives OR, negatives AND-NOT.
 * Several units in a row: AND. `or` binds looser than juxtaposition.
 */
export function parse(input) {
    return new Parser(lex(input)).parseQuery();
}
class Parser {
    toks;
    i = 0;
    constructor(toks) {
        this.toks = toks;
    }
    peek(off = 0) {
        return this.toks[Math.min(this.i + off, this.toks.length - 1)];
    }
    next() {
        const t = this.peek();
        if (t.kind !== "eof")
            this.i++;
        return t;
    }
    isAtom(t) {
        return t.kind === "word" || t.kind === "quoted" || t.kind === "braced";
    }
    isKeyword(t, kw) {
        return t.kind === "word" && t.text.toLowerCase() === kw;
    }
    atSortStart() {
        const a = this.peek();
        const b = this.peek(1);
        const c = this.peek(2);
        return (this.isKeyword(a, "sort") || this.isKeyword(a, "order")) && this.isKeyword(b, "by") && c.kind === "colon";
    }
    atAttrStart() {
        return this.isAtom(this.peek()) && this.peek(1).kind === "colon" && !this.atSortStart();
    }
    parseQuery() {
        let where = null;
        if (!this.atSortStart() && this.peek().kind !== "eof") {
            where = this.parseOr();
        }
        const sort = [];
        if (this.atSortStart()) {
            this.next();
            this.next();
            this.next();
            sort.push(...this.parseSortList());
        }
        const t = this.peek();
        if (t.kind !== "eof")
            throw new ParseError(`unexpected ${describe(t)}`, t.start);
        return { where, sort };
    }
    parseOr() {
        const items = [this.parseAnd()];
        while (this.isKeyword(this.peek(), "or") && !this.atSortStart()) {
            this.next();
            items.push(this.parseAnd());
        }
        return items.length === 1 ? items[0] : { kind: "or", items };
    }
    parseAnd() {
        const items = [];
        for (;;) {
            const t = this.peek();
            if (t.kind === "eof" || t.kind === "rparen" || this.isKeyword(t, "or") || this.atSortStart())
                break;
            items.push(this.parseUnit());
        }
        if (items.length === 0) {
            const t = this.peek();
            throw new ParseError(`expected a condition, got ${describe(t)}`, t.start);
        }
        return items.length === 1 ? items[0] : { kind: "and", items };
    }
    parseUnit() {
        const t = this.peek();
        if (t.kind === "lparen") {
            this.next();
            const inner = this.parseOr();
            const close = this.next();
            if (close.kind !== "rparen")
                throw new ParseError("expected )", close.start);
            return inner;
        }
        if (t.kind === "hash") {
            this.next();
            const a = this.next();
            if (!this.isAtom(a))
                throw new ParseError("expected a name after #", a.start);
            return { kind: "shortcut", name: a.text, span: { start: t.start, end: a.end } };
        }
        if (this.atAttrStart())
            return this.parseAttr();
        if (this.isAtom(t)) {
            this.next();
            return { kind: "text", text: t.text, span: { start: t.start, end: t.end } };
        }
        throw new ParseError(`unexpected ${describe(t)}`, t.start);
    }
    parseAttr() {
        const nameTok = this.next();
        this.next(); // colon
        const values = [this.parseValue()];
        while (this.peek().kind === "comma") {
            this.next();
            values.push(this.parseValue());
        }
        const last = values[values.length - 1];
        return {
            kind: "attr",
            name: nameTok.text,
            values,
            span: { start: nameTok.start, end: last.span.end },
        };
    }
    parseValue() {
        const start = this.peek().start;
        let negated = false;
        if (this.peek().kind === "minus") {
            this.next();
            negated = true;
        }
        if (this.peek().kind === "dotdot") {
            this.next();
            const to = this.next();
            if (!this.isAtom(to))
                throw new ParseError("expected a value after ..", to.start);
            return { kind: "range", from: null, to: to.text, negated, span: { start, end: to.end } };
        }
        const a = this.next();
        if (!this.isAtom(a))
            throw new ParseError(`expected a value, got ${describe(a)}`, a.start);
        if (this.peek().kind === "dotdot") {
            this.next();
            const b = this.peek();
            if (this.isAtom(b) && !this.isKeyword(b, "or") && !this.atSortStart() && this.peek(1).kind !== "colon") {
                this.next();
                return { kind: "range", from: a.text, to: b.text, negated, span: { start, end: b.end } };
            }
            return { kind: "range", from: a.text, to: null, negated, span: { start, end: a.end + 2 } };
        }
        return { kind: "word", text: a.text, negated, span: { start, end: a.end } };
    }
    parseSortList() {
        const out = [];
        for (;;) {
            const name = this.next();
            if (!this.isAtom(name))
                throw new ParseError("expected a field to sort by", name.start);
            let dir = "asc";
            let end = name.end;
            const d = this.peek();
            if (this.isKeyword(d, "asc") || this.isKeyword(d, "desc")) {
                dir = d.text.toLowerCase();
                end = d.end;
                this.next();
            }
            out.push({ attr: name.text, dir, span: { start: name.start, end } });
            if (this.peek().kind !== "comma")
                break;
            this.next();
        }
        return out;
    }
}
function describe(t) {
    return t.kind === "eof" ? "end of query" : JSON.stringify(t.text);
}
/** Inverse of `parse`, used for round-trip tests and for rewriting queries. */
export function print(q) {
    const parts = [];
    if (q.where)
        parts.push(printExpr(q.where, false));
    if (q.sort.length)
        parts.push("sort by: " + q.sort.map((s) => `${atom(s.attr)} ${s.dir}`).join(", "));
    return parts.join(" ");
}
function printExpr(e, insideOr) {
    switch (e.kind) {
        case "and": {
            // `a: 1 .. foo` reads foo as the range end, so a text unit right after an
            // open-ended range is parenthesised to keep it a separate unit.
            const parts = [];
            const flat = flattenAnd(e.items);
            for (const x of flat) {
                let s = printExpr(x, false);
                const prev = parts[parts.length - 1];
                if (prev !== undefined && prev.endsWith("..") && x.kind === "text")
                    s = `(${s})`;
                parts.push(s);
            }
            return parts.join(" ");
        }
        case "or": {
            const s = e.items.map((x) => printExpr(x, true)).join(" or ");
            return `(${s})`;
        }
        case "attr":
            return `${atom(e.name)}: ${e.values.map(printValue).join(", ")}`;
        case "shortcut":
            return `#${atom(e.name)}`;
        case "text":
            return atom(e.text);
    }
}
function flattenAnd(items) {
    return items.flatMap((x) => (x.kind === "and" ? flattenAnd(x.items) : [x]));
}
function printValue(v) {
    const neg = v.negated ? "-" : "";
    if (v.kind === "word")
        return neg + atom(v.text);
    return `${neg}${v.from === null ? "" : atom(v.from) + " "}..${v.to === null ? "" : " " + atom(v.to)}`;
}
const SAFE_WORD = /^[^\s:,(){}"#.-][^\s:,(){}"]*$/;
function atom(s) {
    if (SAFE_WORD.test(s) && !s.includes("..") && !["or", "sort", "order", "asc", "desc"].includes(s.toLowerCase()))
        return s;
    if (!s.includes("}") && s === s.trim())
        return `{${s}}`;
    return `"${s.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}
//# sourceMappingURL=parser.js.map